import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@/lib/database.types';

type Account = Database['public']['Tables']['accounts']['Row'];
type AccountInsert = Database['public']['Tables']['accounts']['Insert'];
type AccountUpdate = Database['public']['Tables']['accounts']['Update'];

export type AccountType = 'asset' | 'liability';

export type AssetSubtype =
  | 'checking'
  | 'savings'
  | 'cash'
  | 'investment'
  | 'real_estate'
  | 'vehicle'
  | 'other_asset'
  | 'watch'
  | 'jewelry'
  | 'precious_metal'
  | 'crypto'
  | 'pea_stock'
  | 'joint_account'
  | 'money_market';
export type LiabilitySubtype = 'credit_card' | 'mortgage' | 'loan' | 'other_liability';
export type AccountSubtype = AssetSubtype | LiabilitySubtype;

export const ASSET_SUBTYPES: AssetSubtype[] = [
  'checking',
  'savings',
  'cash',
  'investment',
  'real_estate',
  'vehicle',
  'other_asset',
  'watch',
  'jewelry',
  'precious_metal',
  'crypto',
  'pea_stock',
  'joint_account',
  'money_market',
];
export const LIABILITY_SUBTYPES: LiabilitySubtype[] = [
  'credit_card',
  'mortgage',
  'loan',
  'other_liability',
];

// Group subtypes for the account list display
export const ACCOUNT_GROUPS = {
  asset: [
    { key: 'bankAccounts', subtypes: ['checking', 'joint_account'] },
    { key: 'livrets', subtypes: ['savings', 'money_market'] },
    { key: 'cash', subtypes: ['cash'] },
    { key: 'investments', subtypes: ['investment'] },
    { key: 'realEstate', subtypes: ['real_estate'] },
    { key: 'vehicles', subtypes: ['vehicle'] },
    { key: 'otherAssets', subtypes: ['other_asset'] },
    { key: 'watchesJewelry', subtypes: ['watch', 'jewelry'] },
    { key: 'preciousMetals', subtypes: ['precious_metal'] },
    { key: 'crypto', subtypes: ['crypto'] },
    { key: 'pea', subtypes: ['pea_stock'] },
  ],
  liability: [
    { key: 'creditCards', subtypes: ['credit_card'] },
    { key: 'mortgages', subtypes: ['mortgage'] },
    { key: 'loans', subtypes: ['loan'] },
    { key: 'otherLiabilities', subtypes: ['other_liability'] },
  ],
} as const;

export interface AccountStats {
  totalAssets: number;
  netWorth: number;
  assetsByGroup: Record<string, number>;
}

export function useAccounts() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Id of the user the list was last loaded for; null before any load. */
  const hasLoadedFor = useRef<string | null>(null);

  const loadAccounts = useCallback(async () => {
    if (!user) return;

    // Only the first load may blank the page. Every later call is a refresh —
    // a transaction changing, the Powens poll, a manual reload — and flipping
    // `loading` there swaps the whole list for the skeleton and back, which
    // reads as the section flashing. Keep the current rows on screen and let
    // them be replaced once the new ones arrive.
    if (!hasLoadedFor.current || hasLoadedFor.current !== user.id) {
      setLoading(true);
    }
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('accounts')
        .select('*')
        .eq('user_id', user.id)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;

      setAccounts(data || []);
    } catch (err) {
      console.error('Error loading accounts:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      // Tracked per user, so signing in as someone else still gets a real
      // loading state instead of briefly showing the previous account's rows.
      hasLoadedFor.current = user.id;
      setLoading(false);
    }
  }, [user]);

  const createAccount = useCallback(
    async (account: Omit<AccountInsert, 'user_id'>) => {
      if (!user) return { success: false, error: 'Not authenticated' };

      try {
        const { data, error: insertError } = await supabase
          .from('accounts')
          .insert([{ ...account, user_id: user.id }])
          .select()
          .single();

        if (insertError) throw insertError;

        await loadAccounts();
        window.dispatchEvent(new CustomEvent('achievements-need-refresh'));
        return { success: true, data };
      } catch (err) {
        console.error('Error creating account:', err);
        return { success: false, error: err };
      }
    },
    [user, loadAccounts]
  );

  const updateAccount = useCallback(
    async (id: string, updates: AccountUpdate) => {
      try {
        const { error: updateError } = await supabase
          .from('accounts')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('id', id);

        if (updateError) throw updateError;

        await loadAccounts();
        return { success: true };
      } catch (err) {
        console.error('Error updating account:', err);
        return { success: false, error: err };
      }
    },
    [loadAccounts]
  );

  const deleteAccount = useCallback(
    async (id: string) => {
      try {
        const account = accounts.find((a) => a.id === id);

        if (account?.source === 'powens') {
          // An aggregated account cannot just be dropped locally: the bank link
          // lives at Powens, and leaving it there means it keeps syncing under a
          // consent the user thinks they revoked. The edge function removes it
          // there first, then cleans up here.
          const { data, error: fnError } = await supabase.functions.invoke('powens-disconnect', {
            body: { account_id: id },
          });

          if (fnError) throw new Error(fnError.message);
          if (data?.error) throw new Error(data.error);
        } else {
          const { error: deleteError } = await supabase.from('accounts').delete().eq('id', id);
          if (deleteError) throw deleteError;
        }

        await loadAccounts();
        window.dispatchEvent(new CustomEvent('achievements-need-refresh'));
        return { success: true };
      } catch (err) {
        console.error('Error deleting account:', err);
        return { success: false, error: err };
      }
    },
    [accounts, loadAccounts]
  );

  const toggleHidden = useCallback(
    async (id: string, isHidden: boolean) => {
      return updateAccount(id, { is_hidden: !isHidden });
    },
    [updateAccount]
  );

  const batchCreateAccounts = useCallback(
    async (
      accountsData: Omit<AccountInsert, 'user_id'>[]
    ): Promise<{ success: boolean; imported: number; errors: string[] }> => {
      if (!user) return { success: false, imported: 0, errors: ['Not authenticated'] };

      const errors: string[] = [];
      let imported = 0;

      // Insert in batches of 50
      for (let i = 0; i < accountsData.length; i += 50) {
        const batch = accountsData.slice(i, i + 50).map((a) => ({
          ...a,
          user_id: user.id,
        }));

        const { error: insertError, data } = await supabase.from('accounts').insert(batch).select();

        if (insertError) {
          errors.push(`Batch ${Math.floor(i / 50) + 1}: ${insertError.message}`);
        } else {
          imported += data?.length || 0;
        }
      }

      await loadAccounts();
      window.dispatchEvent(new CustomEvent('achievements-need-refresh'));
      return { success: errors.length === 0, imported, errors };
    },
    [user, loadAccounts]
  );

  const reorderAccounts = useCallback(
    async (orderedIds: string[]) => {
      // Optimistic update — only reorder accounts in the provided list
      const orderMap = new Map(orderedIds.map((id, i) => [id, i]));
      setAccounts((prev) => {
        return prev.map((acc) => {
          const newOrder = orderMap.get(acc.id);
          if (newOrder !== undefined) {
            return { ...acc, display_order: newOrder };
          }
          return acc;
        });
      });
      try {
        const updates = orderedIds.map((id, index) =>
          supabase.from('accounts').update({ display_order: index }).eq('id', id)
        );
        await Promise.all(updates);
        return { success: true };
      } catch (err) {
        console.error('Error reordering accounts:', err);
        await loadAccounts(); // Rollback on error
        return { success: false, error: err };
      }
    },
    [loadAccounts]
  );

  const calculateStats = useCallback((): AccountStats => {
    const visibleAccounts = accounts.filter((a) => !a.is_hidden);

    const totalAssets = visibleAccounts
      .filter((a) => a.type === 'asset')
      .reduce((sum, a) => sum + a.balance, 0);

    const totalLiabilities = visibleAccounts
      .filter((a) => a.type === 'liability')
      .reduce((sum, a) => sum + Math.abs(a.balance), 0);

    const netWorth = totalAssets - totalLiabilities;

    // Group assets
    const assetsByGroup: Record<string, number> = {};
    for (const group of ACCOUNT_GROUPS.asset) {
      const groupTotal = visibleAccounts
        .filter((a) => a.type === 'asset' && group.subtypes.includes(a.subtype as never))
        .reduce((sum, a) => sum + a.balance, 0);
      if (groupTotal !== 0) {
        assetsByGroup[group.key] = groupTotal;
      }
    }

    return { totalAssets, netWorth, assetsByGroup };
  }, [accounts]);

  useEffect(() => {
    if (user) {
      loadAccounts();
    }
  }, [user, loadAccounts]);

  // Auto-refresh when transactions change (defense-in-depth)
  useEffect(() => {
    const handler = () => {
      loadAccounts();
    };
    window.addEventListener('accounts-need-refresh', handler);
    return () => window.removeEventListener('accounts-need-refresh', handler);
  }, [loadAccounts]);

  // Active accounts: excludes hidden/archived ones
  const activeAccounts = useMemo(() => accounts.filter((a) => !a.is_hidden), [accounts]);

  return {
    accounts,
    activeAccounts,
    loading,
    error,
    loadAccounts,
    createAccount,
    updateAccount,
    deleteAccount,
    toggleHidden,
    batchCreateAccounts,
    reorderAccounts,
    calculateStats,
  };
}
